import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { LookupList, LookupListItem } from '@common/data-models/complex-types/lookup-list';
import { LocationList } from '@common/data-models/complex-types/location-list';
import { UserLocation } from '@common/data-models/complex-types/user-location';
import { AttachmentList, AttachmentListItem } from '@common/data-models/complex-types/attachment-list';
import { Address } from '@common/data-models/complex-types/address';
import { DateRange } from '@common/data-models/complex-types/date-range';
import { Relationship } from '@common/data-models/complex-types/relationship';
import { FieldDef } from '@common/data-models/service/field-def';
import { Table } from '@common/data-models/complex-types/table';
import { ViewStateOrder } from '@common/data-models/complex-types/view-state-order';
import { ObjectTypes } from '@common/enums/object-types';
import { IGenericAccessor } from '@common/interfaces/i-generic-accessor';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { ViewStateFilter } from '@common/data-models/navigation/viewstate-filter';
import { FilterDef } from '@common/data-models/navigation/filter-def';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { DataType } from "@common/enums/data-type";

import { IGlobalTablesService } from '@common/interfaces/i-global-tables-service';
import { ViewStateSequence } from "@common/data-models/navigation/viewstate-sequence";
import { ViewStateRecord } from "@common/data-models/records/viewstate-record";
import { Record } from "@common/data-models/records/record";
import { ViewStateDef } from "@common/data-models/navigation/viewstate-def";
import { ViewStateDelay } from "@common/data-models/navigation/viewstate-delay";

import { ML2HtmlUtils } from '@common/utils/ml-2-html-utils'
import { Util } from '@suite/common/utils/util';

export interface INomWebServicesSerializer<T> {
    deserialize(acc: IGenericAccessor, key: string, type: number): T;
    serialize(data: T, key?: any): any;
}

export class ChecklistSerializer implements INomWebServicesSerializer<MultiValue<string>> {
    private static instance: ChecklistSerializer = null;
    public static get Instance(): ChecklistSerializer {
        if (!ChecklistSerializer.instance) ChecklistSerializer.instance = new ChecklistSerializer();
        return ChecklistSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): MultiValue<string> {
        let value = acc.getRawValue(key);
        let ret = new MultiValue<string>();
        if (Array.isArray(value)) {
            for (let v of value) {
                v = (<string>v).replace(/~C9~/g, "|").replace(/\u0009/g, "|").replace(/;/g, "|");
                for (let prop of (<string>v).split("|")) {
                    if (prop) {
                        ret.set(prop.replace(/[\u00A0]/g, " ").trim());
                    }
                }
            }
            return ret;
        } else {
            if (value) {
                value = (<string>value).replace(/~C9~/g, "|").replace(/\u0009/g, "|").replace(/;/g, "|");
                for (let prop of (<string>value).split("|")) {
                    if (prop) {
                        ret.set(prop.replace(/[\u00A0]/g, " ").trim());
                    }
                }
            }
            return ret;
        }
    }

    public serialize(data: string[]): string {
        let ret = "";
        if (data) {
            for (let prop of data) {
                if (ret != "") ret += "|";
                ret += prop;
            }
        }
        return ret;
    }
}

export class StaffIDListSerializer implements INomWebServicesSerializer<MultiValue<string>> {
    constructor(public globalTablesService: IGlobalTablesService) {
    }
    private static instance: StaffIDListSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): StaffIDListSerializer {
        if (!StaffIDListSerializer.instance) StaffIDListSerializer.instance = new StaffIDListSerializer(globalTablesService);
        return StaffIDListSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): MultiValue<string> {
        let value = acc.getRawValue(key);
        let ret = new MultiValue<string>();
        if (Array.isArray(value)) {
            for (let v of value) {
                v = (<string>v).replace(/~C9~/g, "|").replace(/\u0009/g, "|").replace(/;/g, "|");
                for (let prop of (<string>v).split("|")) {
                    if (prop.trim() == "") continue;
                    prop = prop.replace(key + ".", "");
                    prop = prop.replace(/[\u00A0]/g, " ").trim();
                    // We only keep valid StaffIDs
                    if (this.globalTablesService.Staff[prop]) {
                        ret.set(prop);
                    }
                }
            }
            return ret;
        } else {
            if (value) {
                value = (<string>value).replace(/~C9~/g, "|").replace(/\u0009/g, "|").replace(/;/g, "|");
                for (let prop of (<string>value).split("|")) {
                    if (prop.trim() == "") continue;
                    prop = prop.replace(/[\u00A0]/g, " ").trim();
                    prop = prop.replace(key + ".", "");
                    // We only keep valid StaffIDs
                    if (this.globalTablesService.Staff[prop]) {
                        ret.set(prop);
                    }
                }
            }
            return ret;
        }
    }

    public serialize(data: string[], key: string): string {
        let ret = "";
        if (data && data.length > 0) {
            for (let prop of data) {
                ret += "\u0009" + key + "." + prop;
            }
            ret += "\u0009"
        }
        return ret;
    }
}

export class LocationListComplexSerializer implements INomWebServicesSerializer<LocationList> {
    constructor(public globalTablesService: IGlobalTablesService) {

    }

    private static instance: LocationListComplexSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): LocationListComplexSerializer {
        if (!LocationListComplexSerializer.instance) LocationListComplexSerializer.instance = new LocationListComplexSerializer(globalTablesService);
        return LocationListComplexSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): LocationList {
        let value = acc.getRawValue(key);
        let returnVal: LocationList = new LocationList();
        if (typeof (value) == "string") {
            // decode from string
            let locationsArr: Array<string> = value.split("\r");
            for (let i = 0; i < locationsArr.length; i++) {
                let lat: number;
                let lon: number;
                let deviceId: string;
                let lastUpdate: DateTime;
                let vals: Array<string> = locationsArr[i].split("\t");
                if (vals[i].length && vals[i].length > 0) {
                    lat = parseFloat(vals[0]);
                }
                if (vals[i].length && vals[i].length > 1) {
                    lon = parseFloat(vals[1]);
                }
                if (vals[i].length && vals[i].length > 2) {
                    // we need to parse
                    lastUpdate = new DateTime(vals[2]);
                }
                if (vals[i].length && vals[i].length > 3) {
                    deviceId = vals[3];
                }
                let loc: UserLocation = new UserLocation(lat, lon, deviceId, lastUpdate);
                returnVal.locations.push(loc);
            }
        } else {
            // we have arrays of arrays of ENPSFields objects
            if (value && value.length) {
                for (let i = 0; i < (value as Array<any>).length; i++) {
                    let lat: number;
                    let lon: number;
                    let deviceId: string;
                    let lastUpdate: DateTime;
                    // access latitude
                    if (value[i].length && value[i].length > 0) {
                        lat = parseFloat(value[i][0].FieldValue as string);
                    }
                    if (value[i].length && value[i].length > 1) {
                        lon = parseFloat(value[i][1].FieldValue as string);
                    }
                    if (value[i].length && value[i].length > 2) {
                        // we need to parse
                        lastUpdate = new DateTime(value[i][2].FieldValue as string);
                    }
                    if (value[i].length && value[i].length > 3) {
                        deviceId = value[i][3].FieldValue;
                    }
                    let loc: UserLocation = new UserLocation(lat, lon, deviceId, lastUpdate);
                    returnVal.locations.push(loc);
                }
            }
        }
        return returnVal;
    }

    public serialize(locationList: LocationList): string {
        if (locationList == null) {
            return "";
        }
        return locationList.toString();
    }
}

export class LocationListSerializer implements INomWebServicesSerializer<string> {
    constructor(public globalTablesService: IGlobalTablesService) {

    }

    private static instance: LocationListSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): LocationListSerializer {
        if (!LocationListSerializer.instance) LocationListSerializer.instance = new LocationListSerializer(globalTablesService);
        return LocationListSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): string {
        let value = acc.getRawValue(key);
        let returnVal: string = "";
        if (value && value.length && value.length > 0) {
            for (let i = 0; i < (value[0] as Array<any>).length; i++) {
                returnVal += value[0][i].FieldValue + "\t";
            }
            returnVal += "\r";
        }
        return returnVal;
    }

    public serialize(locationList: string): string {
        return locationList;
    }
}

export class CustomFieldsSerializer implements INomWebServicesSerializer<Array<FieldDef>> {
    constructor(public globalTablesService: IGlobalTablesService) {

    }

    private static instance: CustomFieldsSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): CustomFieldsSerializer {
        if (!CustomFieldsSerializer.instance) CustomFieldsSerializer.instance = new CustomFieldsSerializer(globalTablesService);
        return CustomFieldsSerializer.instance;
    }

    public initialize(value?: Array<string>): Array<FieldDef> {
        let ret: Array<FieldDef> = [];
        if (value && value.length) {
            ret = value.filter((item) => {
                return !!item;
            }).map((item) => {
                const chunks = item.split('-');
                if (chunks && chunks.length > 1) {
                    const newFieldDefs: Array<FieldDef> = []
                    let newField: FieldDef = new FieldDef();
                    Util.fillAddressFields(this.globalTablesService.FieldDefsDict[chunks[0]], newFieldDefs);
                    for (let field of newFieldDefs) {
                        if (field.ID == item) {
                            Object.assign(newField, field);
                            newField.ComplexPropInnerId = chunks[1];
                            return newField;
                        }
                    }
                }
                if (chunks && chunks.length == 1) {
                    return this.globalTablesService.FieldDefsDict[chunks[0]];
                }
                return this.globalTablesService.FieldDefsDict[item];
            });
        }
        return ret;
    }

    public deserialize(acc: IGenericAccessor, key: string): Array<FieldDef> {
        let ids = [];
        try {
            ids = JSON.parse(acc.getRawValue(key));
        } catch (e) { }
        return this.initialize(ids);
    }

    public serialize(fields: Array<FieldDef>): any {
        let ret = JSON.stringify(fields.filter((item: FieldDef) => {
            return !!item;
        }).map((item) => {
            return item.ID;
        }));
        return ret;
    }
}


export class SpecialSerializer implements INomWebServicesSerializer<Table> {

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    private static instance: SpecialSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): SpecialSerializer {
        if (!SpecialSerializer.instance) SpecialSerializer.instance = new SpecialSerializer(globalTablesService);
        return SpecialSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): Table {
        let value = acc.getRawValue(key);
        let ret = new Table();
        let colMetadata = [];

        let specialType: FieldDef = null;

        if (key == "ExternalFiles") {
            specialType = this.globalTablesService.getExternalFilesFieldDef();
        } else if (type == ObjectTypes.ASSIGNMENT) {
            specialType = this.globalTablesService.getSpecialPropertiesFor('Assignment').filter(item => item.ID == key)[0];
        } else if (type == ObjectTypes.TOPIC) {
            specialType = this.globalTablesService.getSpecialPropertiesFor('Topic').filter(item => item.ID == key)[0];
        }
        if (value) {
            if (Array.isArray(value)) {
                if (specialType) {
                    let listItems = this.globalTablesService.getListColumnsForSpecialField(specialType.ID);
                    colMetadata = listItems;
                    for (let rowIndex in value) {
                        for (let listItem in listItems) {
                            let row = value[rowIndex];
                            if (Array.isArray(row)) {
                                if (typeof row[listItem].FieldValue == 'string') {
                                    ret.set(parseInt(rowIndex), parseInt(listItem), row[listItem].FieldValue.replace(/[\u00A0]/g, " "));
                                } else {
                                    ret.set(parseInt(rowIndex), parseInt(listItem), row[listItem].FieldValue);
                                }
                            } else {
                                let propKey = row[listItems[listItem].header.toUpperCase()];
                                if (typeof propKey == 'string') {
                                    ret.set(parseInt(rowIndex), parseInt(listItem), ((propKey == "null") || (!propKey)) ? null : propKey.replace(/[\u00A0]/g, " "));
                                } else {
                                    ret.set(parseInt(rowIndex), parseInt(listItem), ((propKey == "null") || (!propKey)) ? null : propKey);
                                }
                            }
                        }
                    }
                }
            } else {
                if (specialType) {
                    let listItems = this.globalTablesService.getListColumnsForSpecialField(specialType.ID);
                    colMetadata = listItems;
                    for (let item in listItems) {
                        let propKey = value[specialType.ID + '_' + listItems[item].header];
                        if (!propKey) {
                            // Fallback to parent object
                            propKey = acc.getRawValue(specialType.ID + '_' + listItems[item].header.replace(/ /g, ""));
                        }
                        for (let rowIndex in propKey) {
                            if (typeof propKey[rowIndex] == 'string') {
                                ret.set(parseInt(rowIndex), parseInt(item), ((propKey[rowIndex] == "null") || (!propKey[rowIndex])) ? null : propKey[rowIndex].replace(/[\u00A0]/g, " "));
                            } else {
                                ret.set(parseInt(rowIndex), parseInt(item), ((propKey[rowIndex] == "null") || (!propKey[rowIndex])) ? null : propKey[rowIndex]);
                            }

                        }
                    }
                }
            }
        }
        ret.colMetadata = colMetadata;
        ret.addEmptyRowIfNeeded();
        return ret;
    }

    public serialize(table: Table): string {
        let ret = "";
        if (table.data.length > 0) {
            let lastRow = table.data[table.data.length - 1];
            let shouldSkipLastRow: boolean = true;
            for (let i = 0; i < lastRow.length; i++) {
                if (lastRow[i] != null) {
                    shouldSkipLastRow = false;
                    break;
                }
            }
            let last = shouldSkipLastRow ? table.data.length - 1 : table.data.length;
            for (let i = 0; i < last; i++) {
                for (let j = 0; j < table.data[i].length; j++) {
                    let val = table.get(i, j);
                    if (val) {
                        if (j > 0) ret += "\t";
                        ret += String(val);
                    } else {
                        if (j > 0) ret += "\t";
                        //ret += null;
                    }
                }
                if (i < last - 1) {
                    ret += "\r";
                }
            }
        }
        return ret;
    }
}

export class ComboboxSerializer implements INomWebServicesSerializer<string> {
    private static instance: ComboboxSerializer = null;
    public static get Instance(): ComboboxSerializer {
        if (!ComboboxSerializer.instance) ComboboxSerializer.instance = new ComboboxSerializer();
        return ComboboxSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string): string {
        let value = acc.getRawValue(key);

        if (Array.isArray(value)) {
            return (value.length > 0) ? value[0] : null;
        } else {
            return value;
        }
    }

    public serialize(data: string): string {
        return data;
    }
}

export class LookupListSerializer implements INomWebServicesSerializer<LookupList> {

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    private static instance: LookupListSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): LookupListSerializer {
        if (!LookupListSerializer.instance) LookupListSerializer.instance = new LookupListSerializer(globalTablesService);
        return LookupListSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): LookupList {
        let value = acc.getRawValue(key);
        let ret = new LookupList();

        if (value) {
            if (Array.isArray(value)) {
                for (let tagIndex in value) {
                    let tag = value[tagIndex];
                    if (Array.isArray(tag)) {
                        let tagID = tag[0]; //Tag ID
                        //We need just the ID since the rest of the values DisplayName and Description are the same.
                        let tagToAdd = tagID ? tagID.FieldValue : "";

                        let displayName: string = tagToAdd;
                        let description: string = tagToAdd;
                        if (tag.length > 1) {
                            displayName = tag[1] ? tag[1].FieldValue : "";

                        }
                        if (tag.length > 2) {
                            description = tag[2] ? tag[2].FieldValue : "";
                        }
                        if (tagToAdd.length > 0) {
                            let item = new LookupListItem(tagToAdd, displayName, description);
                            if (!ret.hasKey(tagToAdd)) {
                                ret.set(item);
                            }
                        }
                    } else {
                        const tagToAdd = tag.ID; //We need just the ID since the rest of the values DisplayName and Description are the same.
                        const display = tag.DISPLAYNAME;
                        const description = tag.DESCRIPTION;
                        let item = new LookupListItem(tagToAdd, display, description);
                        if (!ret.hasKey(tagToAdd)) {
                            ret.set(item);
                        }
                    }
                }
            } else {
                let idProp = "";
                if (Object.keys(value).some(function (k) { return k.indexOf("ID") > -1 })) {
                    idProp = Object.keys(value).find(function (k) { return k.indexOf("ID") > -1 })
                }
                let tagsIDs = value[idProp];
                if (tagsIDs) {
                    for (let tag of tagsIDs) {
                        let item = new LookupListItem(tag, tag, tag);
                        if (!ret.hasKey(tag)) {
                            ret.set(item);
                        }
                    }
                }
            }
        }
        return ret;
    }

    public serialize(list: LookupList): string {
        let ret = "";
        for (let item of list) {
            ret += item.id + "\t" + item.displayText + "\t" + item.description + "\r";
        }
        return ret;
    }
}

export class AttachmentListSerializer implements INomWebServicesSerializer<AttachmentList> {

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    private static instance: AttachmentListSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): AttachmentListSerializer {
        if (!AttachmentListSerializer.instance) AttachmentListSerializer.instance = new AttachmentListSerializer(globalTablesService);
        return AttachmentListSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): AttachmentList {
        let value = acc.getRawValue(key);
        let table: Table = new Table();
        if (typeof value === "string" && value.length > 0) {
            let rows = value.split("\r");
            for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                let row = rows[rowIndex];
                let cols = row.split("\t");
                for (let colIndex = 0; colIndex < cols.length; colIndex++) {
                    table.set(rowIndex, colIndex, cols[colIndex]);
                }
            }
        } else {
            table = SpecialSerializer.Instance(this.globalTablesService).deserialize(acc, key, type);
        }
        let ret = new AttachmentList();
        if (table) {
            for (let i = 0; i < table.getRowCount(); i++) {
                if (table.get(i, 0) === null) continue;
                ret.items.push(new AttachmentListItem(table.get(i, 0), table.get(i, 1), table.get(i, 2), table.get(i, 3)));
            }
        }
        return ret;
    }

    public serialize(attachmentList: AttachmentList): string {
        let table = new Table();
        if (attachmentList && attachmentList.items) {
            for (let i = 0; i < attachmentList.items.length; i++) {
                let attachment = attachmentList.items[i];
                table.set(i, 0, attachment.database);
                table.set(i, 1, attachment.path);
                table.set(i, 2, attachment.guid);
                table.set(i, 3, attachment.title);
            }
        }
        return SpecialSerializer.Instance(this.globalTablesService).serialize(table);
    }
}

export class AddressSerializer implements INomWebServicesSerializer<Address> {

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    private static instance: AddressSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): AddressSerializer {
        if (!AddressSerializer.instance) AddressSerializer.instance = new AddressSerializer(globalTablesService);
        return AddressSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): Address {
        let value = acc.getRawValue(key);
        let ret = new Address();

        if (value) {
            let listItems = this.globalTablesService.getListColumnsForAddress();
            if (Array.isArray(value)) {
                for (let rowIndex in value) {
                    for (let listItem in listItems) {
                        let row = value[rowIndex];
                        let valueToSet: any;
                        if (Array.isArray(row)) {
                            if (row[listItem] && row[listItem].FieldValue) {
                                valueToSet = row[listItem].FieldValue.replace(/[\u00A0]/g, " ");
                            }
                        }
                        else {
                            valueToSet = row[listItems[listItem].header.toUpperCase()];
                        }
                        if (valueToSet) {
                            this.setAddressPropertyForHeader(ret, listItems[listItem].header.toUpperCase(), valueToSet);
                        } else {
                            if (listItems[listItem].header.toUpperCase() == 'LOCATIONID') {
                                ret.isManualEditMode = true;
                            }
                        }
                    }
                }
            } else {
                for (let item in listItems) {
                    let propKey = value[key + '_' + listItems[item].header];
                    if (!propKey) {
                        // Fallback to parent object
                        propKey = acc.getRawValue(key + '_' + listItems[item].header.replace(/ /g, ""));
                    }
                    for (let rowIndex in propKey) {
                        this.setAddressPropertyForHeader(ret, listItems[item].header.replace(/ /g, "").toUpperCase(), ((propKey[rowIndex] == "null") || (!propKey[rowIndex])) ? null : propKey[rowIndex].replace(/[\u00A0]/g, " "));
                    }
                }
            }
        }
        if (!ret.isManualEditMode) {
            ret.updateFormattedAddress();
        }
        return ret;
    }

    private setAddressPropertyForHeader(addressToUpdate: Address, subField: string, value: any) {
        if (subField == 'COUNTRY') {
            addressToUpdate.country = value;
        } else if (subField == 'STATE') {
            addressToUpdate.state = value;
        } else if (subField == 'CITY') {
            addressToUpdate.city = value;
        } else if (subField == 'ADMINISTRATIVEAREA') {
            addressToUpdate.administrativeArea = value;
        } else if (subField == 'LOCALITY') {
            addressToUpdate.locality = value;
        } else if (subField == 'STREETADDRESS') {
            addressToUpdate.streetAddress = value;
        } else if (subField == 'LOCATIONNAME') {
            addressToUpdate.locationName = value;
        } else if (subField == 'LATITUDE') {
            addressToUpdate.lat = value;
        } else if (subField == 'LONGITUDE') {
            addressToUpdate.long = value;
        } else if (subField == 'LOCATIONID') {
            addressToUpdate.placeId = value;
        } else if (subField == 'FORMATTEDADDRESS') {
            addressToUpdate.formattedAddress = value;
        } else if (subField == 'SHORTSTATE') {
            addressToUpdate.shortState = value;
        } else if (subField == 'SHORTCOUNTRY') {
            addressToUpdate.shortCountry = value;
        } else if (subField == 'POSTALCODE') {
            addressToUpdate.postalCode = value;
        } else if (subField == 'NEIGHBORHOOD') {
            addressToUpdate.neighborhood = value;
        } else if (subField == 'TITLE') {
            addressToUpdate.title = value;
        } else if (subField == 'SUBLOCALITY') {
            addressToUpdate.subLocality = value;
        }
    }

    public serialize(table: Address): string {
        let ret = table.country +
            "\t" + table.administrativeArea +
            "\t" + table.locality +
            "\t" + table.streetAddress +
            "\t" + table.locationName +
            "\t" + table.postalCode +
            "\t" + table.lat +
            "\t" + table.long +
            "\t" + table.placeId +
            "\t" + table.formattedAddress +
            "\t" + table.shortCountry +
            "\t" + table.shortState +
            "\t" + table.title +
            "\t" + table.subLocality +
            "\t" + table.neighborhood +
            "\t" + table.city +
            "\t" + table.state +
            "\r";
        return ret;
    }
}

export class ViewStateFilterSerializer implements INomWebServicesSerializer<ViewStateFilter> {

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    private static instance: ViewStateFilterSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): ViewStateFilterSerializer {
        if (!ViewStateFilterSerializer.instance) ViewStateFilterSerializer.instance = new ViewStateFilterSerializer(globalTablesService);
        return ViewStateFilterSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): ViewStateFilter {
        let value = DataType.fromRaw(acc.getRawValue(key), DataType.STRING)
        return this.initialize(type, value);
    }

    public initialize(type: number, value?: string): ViewStateFilter {
        let ret = new ViewStateFilter();
        let filters: Array<FilterDef> = this.globalTablesService.getFilters(type);

        let filter: any = null;
        if (value) {
            if (typeof value == "string" && value.length > 0) {
                filter = JSON.parse(value.replace(/[\u00A0]/g, " "));
            } else {
                filter = value;
            }
            if (filter.filters) {
                // We have messed up structure
                filter = filter.filters;
            }
        }
        for (let f of filters) {
            let v = (filter && filter[f.ID] && filter[f.ID].value) ? filter[f.ID].value : null;
            if (f.ID == PSTFieldNames.PSTTopicFileDate && !v) {
                v = new DateRange();
                if (type == ViewStateTypes.SEARCH) {
                    v.custom = "@date_range_today_plus_minus_five";
                } else {
                    v.custom = "@date_range_this_week";
                }
            }
            f.IsRequired = (filter && filter[f.ID] && typeof (filter[f.ID].required) !== "undefined") ? !!filter[f.ID].required : true;
            if (f.IsRequired) {
                f.IsOptional = false;
            } else {
                f.IsOptional = (filter && filter[f.ID] && typeof (filter[f.ID].optional) !== "undefined") ? !!filter[f.ID].optional : true;
            }

            f.IsRelationship = (filter && filter[f.ID] && typeof (filter[f.ID].relationship) !== "undefined") ? !!filter[f.ID].relationship : false;
            f.NotUsed = (filter && filter[f.ID] && typeof (filter[f.ID].notused) !== "undefined") ? !!filter[f.ID].notused : false;
            f.IsBlank = (filter && filter[f.ID] && typeof (filter[f.ID].isblank) !== "undefined") ? !!filter[f.ID].isblank : false;
            if (f.NotUsed) {
                f.initialize();
            } else {
                f.initialize(v);
            }
            // if the filter doesn't have a value set, AND it is not explicitly saved in view state, do not show it regardless
            if (!f.isValueSet() && ((filter && !filter[f.ID]) || !filter)) {
                f.NotUsed = true;
            }
            ret.push(f.deepCopy());
        }
        if (this.globalTablesService.populateExtraFiltersCallback) {
            let relationshipFilters: Array<FilterDef> = this.globalTablesService.populateExtraFiltersCallback(type);
            for (let f of relationshipFilters) {
                let v = (filter && filter[f.ID] && filter[f.ID].value) ? filter[f.ID].value : null;
                f.initialize(v);
                // if the filter doesn't have a value set, AND it is not explicitly saved in view state, do not show it regardless
                if (!f.isValueSet() && ((filter && (!filter[f.ID] || filter[f.ID].notused)) || !filter)) {
                    f.NotUsed = true;
                }
                ret.push(f.deepCopy());
            }
        }
        return ret;
    }

    public serialize(data: ViewStateFilter): string {
        return data.toJSON();
    }
}

export class ViewStateOrderSerializer implements INomWebServicesSerializer<ViewStateOrder> {

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    private static instance: ViewStateOrderSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): ViewStateOrderSerializer {
        if (!ViewStateOrderSerializer.instance) ViewStateOrderSerializer.instance = new ViewStateOrderSerializer(globalTablesService);
        return ViewStateOrderSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key?: string, type?: number): ViewStateOrder {
        let value = DataType.fromRaw(acc.getRawValue(key), DataType.STRING)
        let ret = new ViewStateOrder();
        try {
            ret.data = JSON.parse(value);
        } catch (e) {
        }
        return ret;
    }

    public serialize(obj: ViewStateOrder, key?: string, type?: number): string {
        return JSON.stringify(obj.data);
    }
}

export class DateTimeRangeSerializer implements INomWebServicesSerializer<DateRange> {

    constructor() { }

    private static instance: DateTimeRangeSerializer = null;
    public static Instance(): DateTimeRangeSerializer {
        if (!DateTimeRangeSerializer.instance) DateTimeRangeSerializer.instance = new DateTimeRangeSerializer();
        return DateTimeRangeSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key?: string, type?: number): DateRange {
        let value: string = DataType.fromRaw(acc.getRawValue(key), DataType.STRING)
        let ret = new DateRange();

        if (value) {
            let split = value.split('TO');
            if (split.length != 2) {
                split = value.toString().split(';');
                if (split.length == 2) {
                    let fromStr = split[0];
                    let toStr = split[1];
                    if (fromStr && toStr) {
                        ret.custom = null;
                        ret.from = new Date(fromStr)
                        ret.to = new Date(toStr)
                    }
                    if (ret.from.getTime() === (new Date(0)).getTime()) {
                        ret.from = null;
                    }
                    if (ret.to.getTime() === (new Date(3000, 1)).getTime()) {
                        ret.to = null;
                    }
                }
                return ret;
            }
            let fromStr = split[0].replace('[', '').trim();
            let toStr = split[1].replace(']', '').trim();
            if (fromStr && toStr) {
                ret.custom = null;
                ret.from = new Date(fromStr)
                ret.to = new Date(toStr)
            }
            if (ret.from.getTime() === (new Date(0)).getTime()) {
                ret.from = null;
            }
            if (ret.to.getTime() === (new Date(3000, 1)).getTime()) {
                ret.to = null;
            }
        }
        return ret;
    }

    public serialize(obj: DateRange, key?: string, type?: number): string {
        let rangeArray: [Date, Date] = obj.get();

        if (rangeArray) {
            if (!rangeArray[0]) rangeArray[0] = new Date(0);
            if (!rangeArray[1]) rangeArray[1] = new Date(3000, 1);
        }
        return rangeArray[0].toISOString() + ";" + rangeArray[1].toISOString();
    }
}

export class ViewStateOptionsSerializer implements INomWebServicesSerializer<ViewStateOptions> {

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    private static instance: ViewStateOptionsSerializer = null;
    public static Instance(globalTablesService: IGlobalTablesService): ViewStateOptionsSerializer {
        if (!ViewStateOptionsSerializer.instance) ViewStateOptionsSerializer.instance = new ViewStateOptionsSerializer(globalTablesService);
        return ViewStateOptionsSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): ViewStateOptions {
        let value = DataType.fromRaw(acc.getRawValue(key), DataType.STRING)
        return this.initialize(type, key, value);
    }

    public initialize(type: number, key: string, value?: string): ViewStateOptions {
        let ret = new ViewStateOptions();

        if (value) {
            if (typeof value == "string"
                && value.length > 0
                && type == ViewStateTypes.SEARCH
                && key == PSTFieldNames.PSTViewStateSorting) {
                value = JSON.parse(value);
            }
        }

        let options = this.globalTablesService.getOptions(type, key);
        if (options) {
            ret = options.deepCopy();
            ret.initialize(value);
        }

        return ret;
    }

    public serialize(data: ViewStateOptions, key?: string, type?: number): string {
        if (type == ViewStateTypes.SEARCH
            && key == PSTFieldNames.PSTViewStateSorting) {
            return JSON.stringify(data.toJSON());
        } else {
            return data.toJSON();
        }
    }
}

export class ViewStateSequenceSerializer implements INomWebServicesSerializer<ViewStateSequence> {

    private static instance: ViewStateSequenceSerializer = null;
    public static get Instance(): ViewStateSequenceSerializer {
        if (!ViewStateSequenceSerializer.instance) ViewStateSequenceSerializer.instance = new ViewStateSequenceSerializer();
        return ViewStateSequenceSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string): ViewStateSequence {
        return null;
    }

    public initialize(viewstates: Array<ViewStateRecord>, value?: any): ViewStateSequence {
        let ret = new ViewStateSequence();

        let sequence: Array<string> = [];
        if (value) {
            if (typeof value == "string" && value.length > 0) {
                sequence = JSON.parse(value);
            } if (value instanceof ViewStateSequence) {
                for (let vs of value.viewstates) {
                    if (vs.Selected) {
                        sequence.push(vs.Guid);
                    }
                }
            } else {
                sequence = value;
            }
        }
        if (!sequence) sequence = [];

        for (let vs of viewstates) {
            let item = new ViewStateDef(vs.guid, vs.title);
            if (sequence.indexOf(item.Guid) > -1) {
                item.Selected = true;
            }
            ret.push(item);
        }

        return ret;
    }

    public serialize(data: ViewStateSequence): string {
        if (data instanceof ViewStateSequence) {
            return data.toJSON();
        }
        return data;
    }
}

export class ViewStateDelaySerializer implements INomWebServicesSerializer<ViewStateDelay> {

    private static instance: ViewStateDelaySerializer = null;
    public static get Instance(): ViewStateDelaySerializer {
        if (!ViewStateDelaySerializer.instance) ViewStateDelaySerializer.instance = new ViewStateDelaySerializer();
        return ViewStateDelaySerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string): ViewStateDelay {
        let value = DataType.fromRaw(acc.getRawValue(key), DataType.NUMBER)
        return this.initialize(key, value);
    }

    public initialize(key: string, value?: number): ViewStateDelay {
        let ret = new ViewStateDelay();
        ret.Value = value;
        return ret;
    }

    public serialize(data: ViewStateDelay): number {
        return data.toJSON();
    }
}


export class RichTextSerializer implements INomWebServicesSerializer<string> {
    private static instance: RichTextSerializer = null;
    public static get Instance(): RichTextSerializer {
        if (!RichTextSerializer.instance) RichTextSerializer.instance = new RichTextSerializer();
        return RichTextSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): string {
        let value = acc.getRawValue(key);
        let ret: string = "";
        return ML2HtmlUtils.convertML2ToDisplayHtml(value, acc)
    }

    public serialize(data: string): string {
        let ret = "";
        if (data) {
            ret = ML2HtmlUtils.convertDisplayHTMLToML2(data);
        }
        return ret;
    }
}

export class PreviewSerializer implements INomWebServicesSerializer<string> {
    private static instance: PreviewSerializer = null;
    public static get Instance(): PreviewSerializer {
        if (!PreviewSerializer.instance) PreviewSerializer.instance = new PreviewSerializer();
        return PreviewSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): string {
        let value = acc.getRawValue(key);

        if (typeof value === 'string' || value instanceof String) {
            var previewRegex = /\&lt\;objProxyPath\&gt\;(.*)\&lt\;\/objProxyPath\&gt\;/;
            var previewMatches = value.match(previewRegex);
            if (previewMatches && previewMatches.length > 1) {
                return previewMatches[1];
            }
        } else {
            for (let command in value) {
                var previewRegex = /<objProxyPath>(.*)<\/objProxyPath>/;
                var previewMatches: RegExpMatchArray = value[command].match(previewRegex);
                if (previewMatches && previewMatches.length > 1) {
                    //console.log(previewMatches[1]);
                    return previewMatches[1];
                }
            }
        }

        return null;
    }

    public serialize(data: string): string {
        // @TODO
        return null;
    }
}

export class RelationshipSerializer implements INomWebServicesSerializer<Relationship> {
    private static instance: RelationshipSerializer = null;

    constructor(public globalTablesService: IGlobalTablesService) {
    }

    public static Instance(globalTablesService: IGlobalTablesService): RelationshipSerializer {
        if (!RelationshipSerializer.instance) RelationshipSerializer.instance = new RelationshipSerializer(globalTablesService);
        return RelationshipSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): Relationship {
        let value = acc.getRawValue(key);
        let rel = new Relationship();
        if (value && value.length) {
            for (let i = 0; i < value.length; i++) {
                if (value[i].length) {
                    for (let j = 0; j < value[i].length; j++) {
                        if (value[i][j].FieldName == "GUID") {
                            rel.setGuid(value[i][j]["FieldValue"], i);
                        } else if (value[i][j].FieldName == "TYPE") {
                            rel.setType(value[i][j]["FieldValue"], i);
                        }
                    }
                } else {
                    // fallback serializations
                    if (value[i]["GUID"]) {
                        rel.setGuid(value[i]["GUID"], i);
                    }
                    if (value[i]["TYPE"]) {
                        rel.setType(value[i]["TYPE"], i);
                    }
                }
            }
        }
        return rel;
    }

    public serialize(data: Relationship): string {
        let ret = "";
        for (let i = 0; i < data.getRowCount(); i++) {
            ret += data.getGuid(i) + "\t" + data.getType(i) + "\r";
        }
        return ret;
    }
}

export class YoutubeSerializer implements INomWebServicesSerializer<string> {
    private static instance: YoutubeSerializer = null;
    public static get Instance(): YoutubeSerializer {
        if (!YoutubeSerializer.instance) YoutubeSerializer.instance = new YoutubeSerializer();
        return YoutubeSerializer.instance;
    }

    public deserialize(acc: IGenericAccessor, key: string, type: number): string {
        let value = acc.getRawValue(key);

        if (typeof value === 'string' || value instanceof String) {
            var youtubeRegex = /\&lt\;objPath\&gt\;(.*)\&lt\;\/objPath\&gt\;/;
            var youtubeMatches = value.match(youtubeRegex);
            if (youtubeMatches && youtubeMatches.length > 1) {
                if (youtubeMatches[1].indexOf("youtube.com") > -1) {
                    const ret = youtubeMatches[1].replace("http://", "https://")
                    return ret;
                }
            }
        } else {
            for (let command in value) {
                var previewRegex = /<objPath>(.*)<\/objPath>/;
                var youtubeMatches: RegExpMatchArray = value[command].match(previewRegex);
                if (youtubeMatches && youtubeMatches.length > 1) {
                    if (youtubeMatches[1].indexOf("youtube.com") > -1) {
                        const ret = youtubeMatches[1].replace("http://", "https://")
                        return ret;
                    }
                }
            }
        }

        return null;
    }

    public serialize(data: string): string {
        // @TODO
        return null;
    }
}
